/**
 * iOS Safari: touchstart e click possono divergere (ghost click / retargeting).
 * Su button e [role="button"], sintetizza il click dal target del touch.
 */

const INTERACTIVE_SELECTOR = 'button, [role="button"]';
const TAP_MOVE_THRESHOLD_PX = 12;
const GHOST_CLICK_WINDOW_MS = 500;

let touchStartTarget = null;
let touchStartX = 0;
let touchStartY = 0;
let touchMoved = false;
let lastSynthesizedButton = null;
let lastSynthesizedAt = 0;

export const isIosTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return (
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

const getInteractiveControl = (node) => node?.closest?.(INTERACTIVE_SELECTOR) ?? null;

const shouldSkipTarget = (target) => {
  if (!target) return true;
  if (target.disabled || target.getAttribute('aria-disabled') === 'true') return true;
  if (target.closest('[data-skip-ios-click-fix="true"]')) return true;
  return false;
};

export const installIosTouchClickFix = () => {
  if (!isIosTouchDevice()) return () => {};

  const onTouchStart = (event) => {
    if (event.touches.length !== 1) {
      touchStartTarget = null;
      return;
    }
    touchMoved = false;
    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
    touchStartTarget = getInteractiveControl(event.target);
  };

  const onTouchMove = (event) => {
    if (!touchStartTarget || event.touches.length !== 1) return;
    const dx = event.touches[0].clientX - touchStartX;
    const dy = event.touches[0].clientY - touchStartY;
    if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
      touchMoved = true;
    }
  };

  const onTouchEnd = (event) => {
    const target = touchStartTarget;
    touchStartTarget = null;
    if (!target || touchMoved || shouldSkipTarget(target)) return;

    event.preventDefault();
    lastSynthesizedButton = target;
    lastSynthesizedAt = Date.now();
    target.click();
  };

  /** Blocca click fantasma su un altro controllo subito dopo il touch sintetizzato. */
  const onClick = (event) => {
    if (!lastSynthesizedButton || Date.now() - lastSynthesizedAt > GHOST_CLICK_WINDOW_MS) {
      return;
    }
    const clickTarget = getInteractiveControl(event.target);
    if (clickTarget !== lastSynthesizedButton) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onTouchEndClear = () => {
    window.setTimeout(() => {
      lastSynthesizedButton = null;
    }, GHOST_CLICK_WINDOW_MS);
  };

  document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
  document.addEventListener('touchmove', onTouchMove, { capture: true, passive: true });
  document.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
  document.addEventListener('touchend', onTouchEndClear, { capture: true, passive: true });
  document.addEventListener('click', onClick, { capture: true });

  return () => {
    document.removeEventListener('touchstart', onTouchStart, { capture: true });
    document.removeEventListener('touchmove', onTouchMove, { capture: true });
    document.removeEventListener('touchend', onTouchEnd, { capture: true });
    document.removeEventListener('touchend', onTouchEndClear, { capture: true });
    document.removeEventListener('click', onClick, { capture: true });
  };
};
